import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { toast } from 'react-hot-toast'

interface Patient {
  id: string
  patient_id: string
  full_name: string
  date_of_birth: string
  gender: string
  phone: string
  address: string
}

interface Slide {
  id: string
  slide_id: string
  name: string
  case: string
  file_size_mb: number
  created_at: string
}

interface Report {
  id: string
  report_id: string
  patient: string
  patient_name: string
  status: 'DRAFT' | 'PENDING_REVIEW' | 'REVIEWED' | 'FINALIZED' | 'DELIVERED'
  created_at: string
  reviewed_at?: string
  finalized_at?: string
  sent_to_his_lis?: boolean
  exported_pdf?: boolean
}

interface PatientWorkflow {
  patient: Patient
  slides: Slide[]
  reports: Report[]
  workflowStatus: {
    stage: 'new' | 'has_images' | 'has_report' | 'delivered'
    label: string
    color: string
    icon: string
    nextAction: string
    actionLink?: string
  }
}

const WorkflowTracking: React.FC = () => {
  const [patientsWorkflow, setPatientsWorkflow] = useState<PatientWorkflow[]>([])
  const [filteredWorkflow, setFilteredWorkflow] = useState<PatientWorkflow[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [stageFilter, setStageFilter] = useState<string>('all')
  const [selectedPatient, setSelectedPatient] = useState<PatientWorkflow | null>(null)
  const [showWorkflowModal, setShowWorkflowModal] = useState(false)

  const getWorkflowStatus = (_patient: Patient, slides: Slide[], reports: Report[]) => {
    const statuses = {
      new: {
        stage: 'new' as const,
        label: 'Mới tiếp nhận',
        color: 'bg-gray-100 text-gray-700',
        icon: '📋',
        nextAction: 'Cần upload ảnh slide',
        actionLink: '/imaging/upload'
      },
      has_images: {
        stage: 'has_images' as const,
        label: 'Đã có ảnh',
        color: 'bg-blue-100 text-blue-700',
        icon: '🖼️',
        nextAction: 'Xem ảnh và tạo phiếu kết quả',
        actionLink: slides.length > 0 ? `/imaging/viewer/${slides[0].id}` : '/imaging/library'
      },
      has_report: {
        stage: 'has_report' as const,
        label: 'Đã tạo phiếu',
        color: 'bg-purple-100 text-purple-700',
        icon: '📝',
        nextAction: 'Cần trả kết quả',
        actionLink: '/workflow/results'
      },
      delivered: {
        stage: 'delivered' as const,
        label: 'Đã trả kết quả',
        color: 'bg-green-200 text-green-800',
        icon: '✅',
        nextAction: 'Hoàn tất',
        actionLink: undefined
      }
    }

    if (slides.length === 0) return statuses.new
    if (reports.length === 0) return statuses.has_images

    const latestReport = reports.sort((a, b) =>
      new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0]

    if (latestReport.sent_to_his_lis || latestReport.exported_pdf) {
      return statuses.delivered
    }
    // All reports (including finalized) → has_report
    return statuses.has_report
  }

  const fetchWorkflowData = async () => {
    setLoading(true)
    try {
      // Fetch all data including both histopathology and cytology reports
      const [patientsRes, slidesRes, histoReportsRes, cytoReportsRes] = await Promise.all([
        fetch('/api/v1/pathology/patients/?page_size=1000'),
        fetch('/api/v1/imaging/slides/?page_size=1000'),
        fetch('/api/v1/pathology/reports/?page_size=1000'),
        fetch('/api/v1/pathology/cytology-reports/?page_size=1000')
      ])

      if (!patientsRes.ok || !slidesRes.ok) {
        throw new Error('Failed to fetch data')
      }

      const [patientsData, slidesData, histoReportsData, cytoReportsData] = await Promise.all([
        patientsRes.json(),
        slidesRes.json(),
        histoReportsRes.ok ? histoReportsRes.json() : Promise.resolve({ results: [] }),
        cytoReportsRes.ok ? cytoReportsRes.json() : Promise.resolve({ results: [] })
      ])

      const patients = patientsData.results || patientsData
      const slides = slidesData.results || slidesData

      // Combine both types of reports
      const histoReports = (histoReportsData.results || histoReportsData).map((r: any) => ({
        ...r,
        report_type: 'histopathology'
      }))

      const cytoReports = (cytoReportsData.results || cytoReportsData).map((r: any) => ({
        ...r,
        id: r.id.toString(),
        report_id: `CYT-${r.id}`,
        patient: r.patient.toString(),
        patient_name: r.patient_name,
        status: r.status === 'draft' ? 'DRAFT' : r.status === 'finalized' ? 'FINALIZED' : 'PENDING_REVIEW',
        created_at: r.report_date || r.created_at,
        reviewed_at: r.status === 'finalized' ? r.updated_at : undefined,
        finalized_at: r.status === 'finalized' ? r.updated_at : undefined,
        sent_to_his_lis: false,
        exported_pdf: !!r.delivered_at,  // If delivered_at exists, consider it delivered
        report_type: 'cytology'
      }))

      const reports = [...histoReports, ...cytoReports]

      // Map slides by patient_id (slides API already includes patient_id)
      const slidesByPatient = new Map<string, any[]>()
      slides.forEach((slide: any) => {
        if (slide.patient_id) {
          const patientId = slide.patient_id.toString()
          if (!slidesByPatient.has(patientId)) {
            slidesByPatient.set(patientId, [])
          }
          slidesByPatient.get(patientId)!.push(slide)
        }
      })

      // Map reports by patient (ensure consistent string keys)
      const reportsMap = new Map<string, Report[]>()
      reports.forEach((report: Report) => {
        const patientKey = report.patient.toString()
        if (!reportsMap.has(patientKey)) {
          reportsMap.set(patientKey, [])
        }
        reportsMap.get(patientKey)!.push(report)
      })

      const workflow: PatientWorkflow[] = patients.map((patient: Patient) => {
        const patientKey = patient.id.toString()
        const patientReports = reportsMap.get(patientKey) || []
        const patientSlides = slidesByPatient.get(patientKey) || []

        return {
          patient,
          slides: patientSlides,
          reports: patientReports,
          workflowStatus: getWorkflowStatus(patient, patientSlides, patientReports)
        }
      })

      setPatientsWorkflow(workflow)
      setFilteredWorkflow(workflow)
    } catch (error) {
      console.error('Error fetching workflow data:', error)
      toast.error('Không thể tải dữ liệu theo dõi quy trình')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchWorkflowData()
  }, [])

  useEffect(() => {
    let filtered = patientsWorkflow

    if (searchTerm) {
      filtered = filtered.filter(pw =>
        pw.patient.patient_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pw.patient.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pw.patient.phone.includes(searchTerm)
      )
    }

    if (stageFilter !== 'all') {
      filtered = filtered.filter(pw => pw.workflowStatus.stage === stageFilter)
    }

    setFilteredWorkflow(filtered)
  }, [searchTerm, stageFilter, patientsWorkflow])

  const stats = {
    total: patientsWorkflow.length,
    new: patientsWorkflow.filter(pw => pw.workflowStatus.stage === 'new').length,
    has_images: patientsWorkflow.filter(pw => pw.workflowStatus.stage === 'has_images').length,
    has_report: patientsWorkflow.filter(pw => pw.workflowStatus.stage === 'has_report').length,
    delivered: patientsWorkflow.filter(pw => pw.workflowStatus.stage === 'delivered').length,
  }

  const openWorkflowModal = (pw: PatientWorkflow) => {
    setSelectedPatient(pw)
    setShowWorkflowModal(true)
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Theo dõi Quy trình Xử lý Mẫu</h1>
        <p className="mt-1 text-sm text-gray-600">
          Theo dõi trạng thái xử lý mẫu bệnh nhân từ tiếp nhận đến trả kết quả
        </p>
      </div>

      {/* Stats Cards - All in one row */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div
          onClick={() => setStageFilter('all')}
          className={`bg-white rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${stageFilter === 'all' ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="text-sm font-medium text-gray-600">Tổng số</div>
          <div className="mt-1 text-3xl font-semibold text-gray-900">{stats.total}</div>
        </div>

        <div
          onClick={() => setStageFilter('new')}
          className={`bg-gray-50 rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${stageFilter === 'new' ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="text-sm font-medium text-gray-600">📋 Mới tiếp nhận</div>
          <div className="mt-1 text-3xl font-semibold text-gray-700">{stats.new}</div>
        </div>

        <div
          onClick={() => setStageFilter('has_images')}
          className={`bg-blue-50 rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${stageFilter === 'has_images' ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="text-sm font-medium text-blue-600">🖼️ Đã có ảnh</div>
          <div className="mt-1 text-3xl font-semibold text-blue-700">{stats.has_images}</div>
        </div>

        <div
          onClick={() => setStageFilter('has_report')}
          className={`bg-purple-50 rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${stageFilter === 'has_report' ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="text-sm font-medium text-purple-600">📝 Đã tạo phiếu</div>
          <div className="mt-1 text-3xl font-semibold text-purple-700">{stats.has_report}</div>
        </div>

        <div
          onClick={() => setStageFilter('delivered')}
          className={`bg-green-100 rounded-lg shadow p-4 cursor-pointer hover:shadow-md transition-shadow ${stageFilter === 'delivered' ? 'ring-2 ring-blue-500' : ''}`}
        >
          <div className="text-sm font-medium text-green-700">✅ Đã trả kết quả</div>
          <div className="mt-1 text-3xl font-semibold text-green-800">{stats.delivered}</div>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="mb-4 flex gap-4">
        <input
          type="text"
          placeholder="Tìm theo mã BN, họ tên, số điện thoại..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        {stageFilter !== 'all' && (
          <button
            onClick={() => setStageFilter('all')}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Xóa bộ lọc
          </button>
        )}
      </div>

      {/* Workflow Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mã BN
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Họ và Tên
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Số điện thoại
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ảnh
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Phiếu
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Trạng thái
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Cần làm
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Thao tác
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                  Đang tải dữ liệu...
                </td>
              </tr>
            ) : filteredWorkflow.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-6 py-4 text-center text-gray-500">
                  Không tìm thấy dữ liệu
                </td>
              </tr>
            ) : (
              filteredWorkflow.map((pw) => (
                <tr key={pw.patient.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {pw.patient.patient_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {pw.patient.full_name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {pw.patient.phone}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {pw.slides.length} ảnh
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {pw.reports.length > 0 ? '1 phiếu' : '0 phiếu'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${pw.workflowStatus.color}`}>
                      {pw.workflowStatus.icon} {pw.workflowStatus.label}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {pw.workflowStatus.actionLink ? (
                      <Link
                        to={pw.workflowStatus.actionLink}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {pw.workflowStatus.nextAction} →
                      </Link>
                    ) : (
                      <span className="text-green-600 font-medium">{pw.workflowStatus.nextAction}</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => openWorkflowModal(pw)}
                      className="text-blue-600 hover:text-blue-900 font-medium"
                    >
                      Chi tiết
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Workflow Detail Modal */}
      {showWorkflowModal && selectedPatient && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Chi tiết Quy trình</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedPatient.patient.patient_id} - {selectedPatient.patient.full_name}
                </p>
              </div>
              <button
                onClick={() => setShowWorkflowModal(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-6">
              <div className={`px-4 py-3 rounded-lg ${selectedPatient.workflowStatus.color}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-semibold">
                      {selectedPatient.workflowStatus.icon} {selectedPatient.workflowStatus.label}
                    </div>
                    <div className="text-sm mt-1">
                      {selectedPatient.workflowStatus.actionLink ? (
                        <Link
                          to={selectedPatient.workflowStatus.actionLink}
                          className="font-medium underline hover:opacity-80"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {selectedPatient.workflowStatus.nextAction} →
                        </Link>
                      ) : (
                        <span className="font-medium">{selectedPatient.workflowStatus.nextAction}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              {/* Patient Info */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Thông tin Bệnh nhân</h4>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                  <div><span className="font-medium">Mã BN:</span> {selectedPatient.patient.patient_id}</div>
                  <div><span className="font-medium">Họ tên:</span> {selectedPatient.patient.full_name}</div>
                  <div><span className="font-medium">Ngày sinh:</span> {selectedPatient.patient.date_of_birth}</div>
                  <div><span className="font-medium">Giới tính:</span> {selectedPatient.patient.gender}</div>
                  <div><span className="font-medium">SĐT:</span> {selectedPatient.patient.phone}</div>
                  <div><span className="font-medium">Địa chỉ:</span> {selectedPatient.patient.address}</div>
                </div>
              </div>

              {/* Slides */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-3">Ảnh Slides ({selectedPatient.slides.length})</h4>
                <div className="bg-gray-50 rounded-lg p-4 max-h-48 overflow-y-auto">
                  {selectedPatient.slides.length === 0 ? (
                    <p className="text-sm text-gray-500">Chưa có ảnh slide</p>
                  ) : (
                    <ul className="space-y-2 text-sm">
                      {selectedPatient.slides.map(slide => (
                        <li key={slide.id} className="flex justify-between">
                          <span className="font-medium">{slide.slide_id}</span>
                          <span className="text-gray-600">{slide.file_size_mb} MB</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            {/* Reports */}
            <div className="mt-6">
              <h4 className="font-semibold text-gray-900 mb-3">
                Phiếu kết quả (1 case - 1 phiếu)
              </h4>
              <div className="bg-gray-50 rounded-lg p-4">
                {selectedPatient.reports.length === 0 ? (
                  <p className="text-sm text-gray-500">Chưa có phiếu kết quả</p>
                ) : (
                  <div className="bg-white rounded p-4 border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div className="text-sm">
                        <div className="font-medium text-lg">{selectedPatient.reports[0].report_id}</div>
                        <div className="text-gray-600 mt-2">
                          Trạng thái: <span className="font-medium">{selectedPatient.reports[0].status}</span>
                        </div>
                        <div className="text-gray-600">Tạo: {new Date(selectedPatient.reports[0].created_at).toLocaleDateString('vi-VN')}</div>
                        {selectedPatient.reports[0].reviewed_at && (
                          <div className="text-gray-600">Phê duyệt: {new Date(selectedPatient.reports[0].reviewed_at).toLocaleDateString('vi-VN')}</div>
                        )}
                        {selectedPatient.reports[0].finalized_at && (
                          <div className="text-gray-600">Hoàn thành: {new Date(selectedPatient.reports[0].finalized_at).toLocaleDateString('vi-VN')}</div>
                        )}
                        {selectedPatient.reports.length > 1 && (
                          <div className="text-orange-600 mt-2 text-xs">
                            ⚠️ Lưu ý: Phát hiện {selectedPatient.reports.length} phiếu (nên chỉ có 1 phiếu/case)
                          </div>
                        )}
                      </div>
                      <Link
                        to={`/pathology/reports`}
                        className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                      >
                        Xem →
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowWorkflowModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default WorkflowTracking
